import {
  OWASP_RISK_FACTOR_KEYS,
  OWASP_RISK_VECTOR_PREFIX,
  type OwaspRiskCalculation,
  type OwaspRiskData,
  type OwaspRiskFactorKey,
  type OwaspRiskFactorLevel,
  type OwaspRiskFactors,
  type OwaspRiskLevel,
} from './owasp-risk-types';

const RISK_MATRIX: Record<
  OwaspRiskFactorLevel,
  Record<OwaspRiskFactorLevel, OwaspRiskLevel>
> = {
  Low: { Low: 'Note', Medium: 'Low', High: 'Medium' },
  Medium: { Low: 'Low', Medium: 'Medium', High: 'High' },
  High: { Low: 'Medium', Medium: 'High', High: 'Critical' },
};

function clampFactorScore(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(9, Math.max(0, value));
}

function average(values: readonly number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function getOwaspRiskFactorLevel(score: number): OwaspRiskFactorLevel {
  if (score < 3) {
    return 'Low';
  }
  if (score < 6) {
    return 'Medium';
  }
  return 'High';
}

export function getOwaspRiskLevelFromMatrix({
  likelihoodLevel,
  impactLevel,
}: {
  likelihoodLevel: OwaspRiskFactorLevel;
  impactLevel: OwaspRiskFactorLevel;
}): OwaspRiskLevel {
  return RISK_MATRIX[likelihoodLevel][impactLevel];
}

export function calculateOwaspRisk(
  factors: OwaspRiskFactors,
): OwaspRiskCalculation {
  const normalizedFactors = normalizeOwaspRiskFactors(factors);
  const threatAgentScore = average([
    normalizedFactors.SL,
    normalizedFactors.M,
    normalizedFactors.O,
    normalizedFactors.S,
  ]);
  const vulnerabilityScore = average([
    normalizedFactors.ED,
    normalizedFactors.EE,
    normalizedFactors.A,
    normalizedFactors.ID,
  ]);
  const technicalImpactScore = average([
    normalizedFactors.LC,
    normalizedFactors.LI,
    normalizedFactors.LA,
    normalizedFactors.LAc,
  ]);
  const businessImpactScore = average([
    normalizedFactors.FD,
    normalizedFactors.RD,
    normalizedFactors.NC,
    normalizedFactors.PV,
  ]);

  const likelihoodScore = average([threatAgentScore, vulnerabilityScore]);
  const impactScore = average([technicalImpactScore, businessImpactScore]);
  const likelihoodLevel = getOwaspRiskFactorLevel(likelihoodScore);
  const impactLevel = getOwaspRiskFactorLevel(impactScore);
  const level = getOwaspRiskLevelFromMatrix({ likelihoodLevel, impactLevel });
  const score = Number(((likelihoodScore + impactScore) / 2).toFixed(2));

  return {
    likelihoodScore: Number(likelihoodScore.toFixed(2)),
    impactScore: Number(impactScore.toFixed(2)),
    likelihoodLevel,
    impactLevel,
    score,
    level,
  };
}

export function normalizeOwaspRiskFactors(
  factors: Partial<OwaspRiskFactors>,
): OwaspRiskFactors {
  return OWASP_RISK_FACTOR_KEYS.reduce<OwaspRiskFactors>((acc, key) => {
    acc[key] = clampFactorScore(factors[key] ?? 0);
    return acc;
  }, {} as OwaspRiskFactors);
}

export function generateOwaspRiskVector(factors: OwaspRiskFactors): string {
  const normalizedFactors = normalizeOwaspRiskFactors(factors);
  const segments = OWASP_RISK_FACTOR_KEYS.map(
    (key) => `${key}:${normalizedFactors[key]}`,
  );
  return `${OWASP_RISK_VECTOR_PREFIX}:${segments.join('/')}`;
}

export function parseOwaspRiskVector(vector: string): OwaspRiskFactors | null {
  const trimmed = vector.trim();
  if (!trimmed.startsWith(`${OWASP_RISK_VECTOR_PREFIX}:`)) {
    return null;
  }

  const body = trimmed.slice(`${OWASP_RISK_VECTOR_PREFIX}:`.length);
  const segments = body.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  const parsed: Partial<OwaspRiskFactors> = {};
  for (const segment of segments) {
    const separatorIndex = segment.indexOf(':');
    if (separatorIndex <= 0) {
      continue;
    }

    const key = segment.slice(0, separatorIndex) as OwaspRiskFactorKey;
    if (!OWASP_RISK_FACTOR_KEYS.includes(key)) {
      continue;
    }

    const rawValue = segment.slice(separatorIndex + 1);
    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue)) {
      continue;
    }

    parsed[key] = numericValue;
  }

  const hasAllFactors = OWASP_RISK_FACTOR_KEYS.every(
    (key) => parsed[key] !== undefined,
  );
  if (!hasAllFactors) {
    return null;
  }

  return normalizeOwaspRiskFactors(parsed);
}

export function buildOwaspRiskDataFromVector(
  vector: string,
): OwaspRiskData | null {
  const factors = parseOwaspRiskVector(vector);
  if (!factors) {
    return null;
  }

  const calculation = calculateOwaspRisk(factors);
  return {
    score: String(calculation.score),
    vector: generateOwaspRiskVector(factors),
    level: calculation.level,
  };
}

export function mapOwaspRiskLevelToFindingSeverity(
  level: OwaspRiskLevel,
): 'Critical' | 'High' | 'Medium' | 'Low' | 'Info' {
  switch (level) {
    case 'Critical':
      return 'Critical';
    case 'High':
      return 'High';
    case 'Medium':
      return 'Medium';
    case 'Low':
      return 'Low';
    case 'Note':
    default:
      return 'Info';
  }
}

const owaspRiskLevels: readonly OwaspRiskLevel[] = [
  'Note',
  'Low',
  'Medium',
  'High',
  'Critical',
];

export function isOwaspRiskLevel(value: string): value is OwaspRiskLevel {
  return owaspRiskLevels.includes(value as OwaspRiskLevel);
}

export function resolveOwaspRiskBadgeSeverity(
  level: string,
): 'Critical' | 'High' | 'Medium' | 'Low' | 'Info' {
  if (isOwaspRiskLevel(level)) {
    return mapOwaspRiskLevelToFindingSeverity(level);
  }
  return 'Low';
}
